import { useCallback, useState } from 'react';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { Typography, Button, Box, ListItemText, Card, Tooltip, IconButton } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { getNotificationLocale, useResolvedParams } from './utils';
import { PriorityIcon } from 'components/common/PriorityIcon';
import ReactIntlWrapper from 'components/i18n/ReactIntlWrapper';
import {
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
  useNotificationCount,
  useNotificationsStorage,
} from 'store/registry/useNotificationsStorage';
import { fToNow } from 'utils/format-time';
import { Iconify } from 'components/iconify';
import { usePaginationPreference } from 'hooks/usePaginationPreference';
import { useNavigate } from 'react-router';

function NotificationText({ resourceId, params, timeStamp, api }: {
  resourceId: string;
  params: any[];
  timeStamp: string;
  api: Api;
}) {
  const resolvedParams = useResolvedParams(params, api);
  const localeText = getNotificationLocale(resourceId, resolvedParams);
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <ListItemText
        primary={localeText}
        secondary={
          <Typography
            variant="caption"
            sx={{ mt: 0.5, gap: 0.5, display: 'flex', alignItems: 'center', color: 'text.disabled' }}
          >
            <Iconify width={14} icon="solar:clock-circle-outline" />
            {fToNow(new Date(timeStamp))}
          </Typography>
        }
      />
    </div>
  );
}

const Notifications = () => {
  const api: Api = useApi();
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [allMarked, setAllMarked] = useState(false);

  const [paginationModel, setPaginationModel] = usePaginationPreference('notifications', 10);
  const toast = useToastMessageQueue();

  const { data, isLoading } = useNotificationsStorage(
    paginationModel.page,
    paginationModel.pageSize
  );
  const { mutateAsync: markAsRead } = useMarkAsReadMutation();
  const { mutateAsync: markAllAsRead } = useMarkAllAsReadMutation();
  const { data: notificationCount } = useNotificationCount();

  const handleMarkItemAsRead = async (read: string, timeStamp: string) => {
    try {
      await markAsRead(timeStamp);
      return true;
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      return false;
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setAllMarked(!allMarked);
      return true;
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      return false;
    }
  };

  const MarkAsReadButton = ({
    read,
    timeStamp,
  }: {
    read: string;
    timeStamp: string;
  }) => {
    const [markedAsRead, setMarkedAsRead] = useState(read);

    const handleClick = async () => {
      if (markedAsRead === '1') return;
      const success = await handleMarkItemAsRead(markedAsRead, timeStamp);
      if (success) {
        setMarkedAsRead('1');
      }
    };

    return (
      <Button
        onClick={handleClick}
        variant="text"
        color="primary"
        sx={{ ...(markedAsRead === '1' && { pointerEvents: 'none' }) }}
      >
        {markedAsRead === '1' ? <BookmarkIcon /> : <BookmarkBorderIcon />}
      </Button>
    );
  };

  const onPaginationModelChange = useCallback(
    (model: { pageSize: number; page: number }) => {
      setPaginationModel(model);
    },
    [setPaginationModel]
  );

  const columns: GridColDef[] = [
    {
      field: 'priority',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const parsed = JSON.parse(params.row.message);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <PriorityIcon priority={parsed.priority} />
          </Box>
        );
      },
    },
    {
      field: 'resourceId',
      headerName: ReactIntlWrapper.get('notifications'),
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        const parsed = JSON.parse(params.row.message);
        return (
          <NotificationText
            resourceId={parsed.message?.ResourceID}
            params={parsed.message?.Params || []}
            timeStamp={params.row.timeStamp}
            api={api}
          />
        );
      },
    },
    {
      field: 'read',
      headerName: ReactIntlWrapper.get('markAsRead'),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <MarkAsReadButton read={params.row.read} timeStamp={params.row.timeStamp} />
      ),
    },
    {
      field: 'action',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => {
        const parsed = JSON.parse(params.row.message);
        const rawAction = parsed.message?.Action;
        const action: string | undefined = typeof rawAction === 'string' ? rawAction.trim() || undefined : undefined;
        if (!action) return null;
        return (
          <Tooltip title={ReactIntlWrapper.get('viewDetails')} arrow>
            <IconButton size="small" onClick={() => navigate(action)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography color="primary" variant="h2" component="h2">
            {ReactIntlWrapper.get('notifications')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button color="primary" variant="text" size="small" onClick={handleMarkAllAsRead}>
            {allMarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            <Typography ml={0.5}>{ReactIntlWrapper.get('markAll')}</Typography>
          </Button>
        </Box>

        <Box style={{ width: '100%', display: 'flex' }}>
          {isLoading && <Spinner />}
          {data && (
            <DataGrid
              key={`datagrid-${paginationModel.page}-${paginationModel.pageSize}`}
              rows={data.queryResult || []}
              getRowId={(row) => row.timeStamp}
              columns={columns}
              loading={isLoading}
              rowCount={notificationCount}
              rowHeight={68}
              onPaginationModelChange={onPaginationModelChange}
              paginationMode="server"
              paginationModel={paginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
            />
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default Notifications;
